import { useNavigate } from 'react-router-dom';
import {
  Tag, ExternalLink, Check, Minus, TrendingUp, Sparkles, ArrowRight, Info,
} from 'lucide-react';
import { palette, admin, transition, fontFamily } from '../../../styles/tokens';
import { SettingsSubPage } from '../components/SettingsSubPage';
import { EmptyState } from '../../../components/common/EmptyState';
import { useServicesPricing } from '../hooks/useServicesPricing';
import type { ServicePricingSummary } from '../hooks/useServicesPricing';

// ── Toggle ──

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <div onClick={onToggle} style={{
      width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
      background: on ? palette.accent : palette.g300,
      padding: 2, display: 'flex', alignItems: 'center',
      transition: `background ${transition.fast}`,
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 8, background: palette.white,
        marginLeft: on ? 16 : 0, transition: `margin-left ${transition.fast}`,
      }} />
    </div>
  );
}

// ── Tier badge ──

function TierBadge({ tier }: { tier: string }) {
  if (tier === 'business' || tier === 'trial') {
    return <span style={{ ...badge, background: palette.greenWash, color: palette.green }}>Smart fill-rate pricing</span>;
  }
  if (tier === 'pro') {
    return <span style={{ ...badge, background: palette.accentWash, color: palette.accent }}>Peak & off-peak windows</span>;
  }
  return <span style={{ ...badge, background: palette.g100, color: palette.g500 }}>Not available</span>;
}

const badge: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8, whiteSpace: 'nowrap',
};

// ── Dynamic pricing preview ──

function DynamicPreview({ svc }: { svc: ServicePricingSummary | null }) {
  const name = svc?.name ?? 'Gel Manicure';
  const base = svc?.price ?? 50;
  const min = svc?.min_price ?? 42;
  const dur = svc?.duration_minutes ?? 45;
  const saving = base - min;
  const isExample = !svc;

  return (
    <div style={{ border: `1px solid ${palette.g200}`, borderRadius: 10, padding: '14px 16px', background: palette.white }}>
      {isExample && <div style={{ fontSize: 10, color: palette.g400, marginBottom: 6, fontStyle: 'italic' }}>Example preview</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>{name}</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: palette.g800 }}>${min}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
        <span style={{ fontSize: 12, color: palette.g500 }}>{dur} min</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: palette.g400, textDecoration: 'line-through' }}>was ${base}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: palette.green, background: palette.greenWash, padding: '2px 8px', borderRadius: 6 }}>
            Save ${saving}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Intro offer preview ──

function IntroPreview({ svc }: { svc: ServicePricingSummary | null }) {
  const name = svc?.name ?? 'Gel Manicure';
  const base = svc?.price ?? 50;
  const intro = svc?.intro_price ?? 35;
  const dur = svc?.duration_minutes ?? 45;
  const isExample = !svc;

  return (
    <div style={{ border: `1px solid ${palette.g200}`, borderRadius: 10, padding: '14px 16px', background: palette.white }}>
      {isExample && <div style={{ fontSize: 10, color: palette.g400, marginBottom: 6, fontStyle: 'italic' }}>Example preview</div>}
      <div style={{ fontSize: 12, fontWeight: 600, color: palette.accent, marginBottom: 6 }}>New here?</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>{name}</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: palette.accent }}>${intro}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
        <span style={{ fontSize: 12, color: palette.g500 }}>{dur} min</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: palette.g400 }}>First visit price</span>
          <span style={{ fontSize: 12, color: palette.g400 }}>Usually ${base}</span>
        </div>
      </div>
    </div>
  );
}

// ── Service row in summary table ──

function ServiceRow({ svc, bizDynamic, navigate }: {
  svc: ServicePricingSummary; bizDynamic: boolean; navigate: (p: string) => void;
}) {
  const dynOn = bizDynamic && svc.enable_dynamic_pricing;
  const introOn = svc.intro_enabled;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1.5fr 0.8fr 0.7fr 0.9fr 0.7fr 0.8fr 0.4fr',
      alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${palette.g100}`, fontSize: 13,
    }}>
      <span style={{ fontWeight: 500, color: palette.g800 }}>{svc.name}</span>
      <span style={{ color: palette.g700 }}>${svc.price}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {dynOn ? <Check size={13} color={palette.green} strokeWidth={2.5} /> : <Minus size={13} color={palette.g400} />}
        <span style={{ color: dynOn ? palette.green : palette.g400 }}>{dynOn ? 'On' : 'Off'}</span>
      </span>
      <span style={{ color: palette.g500 }}>
        {dynOn && svc.min_price != null && svc.max_price != null
          ? `$${svc.min_price}–$${svc.max_price}` : '\u2014'}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {introOn ? <Check size={13} color={palette.green} strokeWidth={2.5} /> : <Minus size={13} color={palette.g400} />}
        <span style={{ color: introOn ? palette.green : palette.g400 }}>{introOn ? 'On' : 'Off'}</span>
      </span>
      <span style={{ color: palette.g500 }}>{introOn && svc.intro_price != null ? `$${svc.intro_price}` : '\u2014'}</span>
      <button onClick={() => navigate('/services')} style={{
        background: 'none', border: 'none', fontSize: 12, fontWeight: 500,
        color: palette.accent, cursor: 'pointer', fontFamily, padding: 0,
      }}>Edit</button>
    </div>
  );
}

// ── Main page ──

export function ServicesPricingPage() {
  const navigate = useNavigate();
  const {
    dynamicPricingEnabled, toggleDynamicPricing, tier,
    services, loading, previewService, introPreviewService, bookingSlug,
  } = useServicesPricing();

  const canUseDynamic = tier === 'pro' || tier === 'business' || tier === 'trial';

  return (
    <SettingsSubPage title="Services & pricing" description="Dynamic pricing, intro offers, and your service catalog">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Section 1: How pricing works */}
        <div style={card}>
          <div style={cardHeader}>How pricing works</div>
          <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Dynamic pricing */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <TrendingUp size={16} color={palette.accent} strokeWidth={1.8} />
                <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Dynamic pricing</span>
                <TierBadge tier={tier} />
              </div>
              <p style={explainer}>
                When your schedule fills up, prices rise within the range you set.
                When it's quiet, prices drop to attract bookings.
                You set the floor and the ceiling — CrewBit handles the math.
              </p>
              <div style={howList}>
                <div style={howItem}><Info size={12} color={palette.g400} /> You set a minimum and maximum price for each service</div>
                <div style={howItem}><Info size={12} color={palette.g400} /> During peak hours (times you define), prices go up</div>
                <div style={howItem}><Info size={12} color={palette.g400} /> During off-peak hours, prices drop with a "Save $X" badge</div>
                {(tier === 'business' || tier === 'trial') && (
                  <div style={howItem}><Info size={12} color={palette.g400} /> Prices also adjust automatically based on how full your schedule is</div>
                )}
              </div>
            </div>

            {/* Intro offers */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Sparkles size={16} color={palette.accent} strokeWidth={1.8} />
                <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Intro offers</span>
              </div>
              <p style={explainer}>
                Set a special price for first-time clients. CrewBit detects new clients by
                their phone number — returning clients see your regular price.
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Visual previews */}
        <div style={card}>
          <div style={cardHeader}>What your clients see</div>
          <div style={{ padding: '0 20px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: palette.g500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Dynamic pricing
              </div>
              <DynamicPreview svc={previewService} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: palette.g500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Intro offer
              </div>
              <IntroPreview svc={introPreviewService} />
            </div>
          </div>
        </div>

        {/* Section 3: Service pricing summary */}
        <div style={card}>
          <div style={{ ...cardHeaderRow, padding: '14px 20px', borderBottom: `1px solid ${palette.g100}` }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Service pricing</span>
            {canUseDynamic && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: palette.g500 }}>Dynamic pricing</span>
                <Toggle on={dynamicPricingEnabled} onToggle={toggleDynamicPricing} />
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: palette.g400 }}>Loading...</div>
          ) : services.length === 0 ? (
            <EmptyState
              icon={<Tag size={20} />}
              title="No services yet"
              subtitle="Add your first service in Services → New, then return here to configure pricing."
              actionLabel="Add your first service"
              onAction={() => navigate('/services')}
            />
          ) : (
            <>
              {/* Table header */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1.5fr 0.8fr 0.7fr 0.9fr 0.7fr 0.8fr 0.4fr',
                padding: '8px 16px', fontSize: 11, fontWeight: 600, color: palette.g400,
                textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${palette.g100}`,
              }}>
                <span>Service</span><span>Price</span><span>Dynamic</span>
                <span>Range</span><span>Intro</span><span>Intro $</span><span />
              </div>
              {services.map(svc => (
                <ServiceRow key={svc.id} svc={svc} bizDynamic={dynamicPricingEnabled} navigate={navigate} />
              ))}
            </>
          )}
        </div>

        {/* Section 4: Quick actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <button onClick={() => navigate('/services')} style={actionCard}>
            <Tag size={20} color={palette.accent} strokeWidth={1.8} />
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Manage services</span>
            <span style={{ fontSize: 12, color: palette.g500 }}>Add, edit, and configure pricing</span>
            <ArrowRight size={14} color={palette.g400} style={{ marginLeft: 'auto' }} />
          </button>
          <a
            href={bookingSlug ? `https://app.crewbit.ca/book/${bookingSlug}` : '#'}
            target="_blank" rel="noopener noreferrer"
            style={{ ...actionCard, textDecoration: 'none' }}
          >
            <ExternalLink size={20} color={palette.accent} strokeWidth={1.8} />
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>View booking page</span>
            <span style={{ fontSize: 12, color: palette.g500 }}>See how pricing appears to clients</span>
            <ArrowRight size={14} color={palette.g400} style={{ marginLeft: 'auto' }} />
          </a>
        </div>
      </div>
    </SettingsSubPage>
  );
}

// ── Shared styles ──

const card: React.CSSProperties = {
  background: palette.white, borderRadius: admin.cardRadius,
  border: `1px solid ${admin.border}`, overflow: 'hidden',
};

const cardHeader: React.CSSProperties = {
  padding: '14px 20px', borderBottom: `1px solid ${palette.g100}`,
  fontSize: 14, fontWeight: 600, color: palette.g800,
};

const cardHeaderRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};

const explainer: React.CSSProperties = {
  fontSize: 13, color: palette.g600, lineHeight: 1.6, margin: '4px 0 8px',
};

const howList: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
};

const howItem: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: palette.g500,
};

const actionCard: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  padding: '16px 18px', background: palette.white,
  borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`,
  cursor: 'pointer', fontFamily, textAlign: 'left',
  transition: `border-color ${transition.fast}`,
};
